/**
 * HustleRoundupLandingPage — marketing landing on `/`.
 * Hero + feature grid + CTAs to instant meet / schedule / join.
 */
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Video, Calendar, KeyRound, Shield, MessageSquare, Sparkles,
  ArrowRight, Radio, History as HistoryIcon,
} from "lucide-react";
import { fetchMe, clearToken } from "@/lib/api";

const FEATURES = [
  { icon: Radio,          title: "Instant + scheduled rooms",   desc: "Persistent rooms with short join codes, passcodes, and calendar-aware scheduling." },
  { icon: Shield,         title: "Host controls",               desc: "Waiting room, lock, mute-all, kick, co-host promotion — all audit-logged." },
  { icon: MessageSquare,  title: "Persistent chat",             desc: "Real-time WebSocket chat that survives the meeting. Searchable history." },
  { icon: Video,          title: "Cloud recording",             desc: "One-tap LiveKit Egress recording, uploaded direct to Cloudflare R2." },
  { icon: Sparkles,       title: "AI transcript + summary",     desc: "Whisper transcribes, Gemini 2.0 Flash writes the summary and action items." },
  { icon: HistoryIcon,    title: "Meeting history",             desc: "Every past meeting archived — participants, chat, recording, transcript." },
];

export default function HustleRoundupLandingPage() {
  const [me, setMe] = useState(null);
  const nav         = useNavigate();

  useEffect(() => { (async () => setMe(await fetchMe()))(); }, []);

  const logout = () => { clearToken(); setMe(null); };

  return (
    <div
      data-testid="hustleroundup-landing"
      className="min-h-screen bg-stone-950 text-white overflow-x-hidden"
    >
      {/* Top bar */}
      <header className="border-b border-stone-900/80 backdrop-blur-md sticky top-0 z-40 bg-stone-950/70">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <Video className="w-6 h-6 text-emerald-400" />
            <span className="text-lg font-bold tracking-tight">HustleRoundup</span>
          </Link>
          <nav className="flex items-center gap-2 text-sm">
            {me ? (
              <>
                <Link to="/history"
                      data-testid="nav-history"
                      className="px-3 py-2 rounded-md text-stone-300 hover:text-white hover:bg-stone-900">History</Link>
                <Link to="/schedule"
                      data-testid="nav-schedule"
                      className="px-3 py-2 rounded-md text-stone-300 hover:text-white hover:bg-stone-900">Schedule</Link>
                <Link to="/admin/hustleroundup"
                      data-testid="nav-admin"
                      className="px-3 py-2 rounded-md text-stone-300 hover:text-white hover:bg-stone-900">Admin</Link>
                <span className="hidden md:inline text-xs text-stone-500 ml-2">{me.email}</span>
                <button onClick={logout}
                        data-testid="nav-logout"
                        className="ml-2 px-3 py-2 rounded-md text-xs text-stone-400 hover:text-white">Sign out</button>
              </>
            ) : (
              <Link to="/login"
                    data-testid="nav-login"
                    className="px-3 py-2 rounded-md text-stone-300 hover:text-white hover:bg-stone-900">Sign in</Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative px-6 pt-20 pb-24"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 25%, rgba(16,185,129,0.18), transparent 45%)," +
            "radial-gradient(circle at 85% 60%, rgba(59,130,246,0.10), transparent 40%)",
        }}
      >
        <div className="max-w-5xl mx-auto text-left">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[11px] uppercase tracking-widest mb-6"
            data-testid="landing-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Sprint 2 · LiveKit + Whisper + Gemini
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6 max-w-3xl">
            Meetings that don&apos;t <span className="text-emerald-400">need Zoom</span>.
            <br className="hidden sm:block" />
            Live, recorded, summarised — <span className="text-stone-400">inside HustleSuite</span>.
          </h1>
          <p className="text-lg text-stone-400 max-w-2xl leading-relaxed mb-10">
            One-click video meetings powered by LiveKit Cloud, with waiting-room, cloud recording,
            persistent chat, and AI-generated summaries wired straight into your agency workspace.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="cta-start-meeting"
              onClick={() => nav(me ? "/meet" : "/login?next=/meet")}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold"
            >
              <Video className="w-4 h-4" /> Start a meeting
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              type="button"
              data-testid="cta-schedule"
              onClick={() => nav(me ? "/schedule" : "/login?next=/schedule")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-stone-700 hover:border-stone-500 text-white"
            >
              <Calendar className="w-4 h-4" /> Schedule for later
            </button>
            <JoinByCode />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 pb-24 border-t border-stone-900/70">
        <div className="max-w-6xl mx-auto pt-16">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-400/80 mb-3">What ships in v1</div>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-12 max-w-3xl">
            Everything a mid-agency needs. Nothing you have to leave the platform for.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-${i}`}
                className="p-6 rounded-xl border border-stone-900 bg-stone-900/40 hover:border-stone-700 transition-colors"
              >
                <f.icon className="w-6 h-6 text-emerald-400 mb-4" />
                <div className="font-semibold mb-1.5">{f.title}</div>
                <div className="text-sm text-stone-400 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="px-6 py-8 border-t border-stone-900/70 text-stone-500 text-xs">
        <div className="max-w-6xl mx-auto flex justify-between">
          <span>HustleRoundup · a HustleSuite module</span>
          <span>© 2026</span>
        </div>
      </footer>
    </div>
  );
}

function JoinByCode() {
  const [code, setCode] = useState("");
  const nav = useNavigate();
  const go = () => {
    const c = code.trim().replace(/\s+/g, "");
    if (c) nav(`/join/${encodeURIComponent(c)}`);
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        data-testid="cta-join-code-input"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && go()}
        placeholder="Join with code (e.g. 123-456-789)"
        className="px-4 py-3 rounded-full bg-stone-900 border border-stone-800 focus:border-emerald-500 outline-none text-sm min-w-[260px]"
      />
      <button
        type="button"
        data-testid="cta-join-code-btn"
        onClick={go}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-stone-700 hover:border-emerald-500 text-white"
      >
        <KeyRound className="w-4 h-4" /> Join
      </button>
    </div>
  );
}
